import { EmbeddedWebConsoleAppEventHandlers } from './embedded-web-console-app-event-handlers';
import { WebConsoleAppInterface } from './web-console-container.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { rcEventRoutes, RemoteControlCloseTypes } from 'common/common/constants/remote-control';

interface RemoteControlGlobalKeyboardSubscriptionEvent {
  globalKeyboardSubscription: boolean;
}

export class RemoteControlEventHandlers implements EmbeddedWebConsoleAppEventHandlers {
  private _boundNavigationChangeRequest: Function = null;
  private _boundWorkflowEnded: Function = null;
  private _boundHandleSessionInitiated: Function = null;
  private _boundHandleInputTrackerStateChanges: Function = null;

  constructor(private _embeddedApp: WebConsoleAppInterface, private RxEmitter: RxEmitter, private _workItem: ChatItem, private bcRestApi) {
  }

  public registerEvents() {
    this._boundNavigationChangeRequest = this._handleNavigationChangeRequest.bind(this);
    this._boundHandleSessionInitiated = this._handleSessionInitiated.bind(this);
    this._boundWorkflowEnded = this._handleWorkflowEnded.bind(this);
    this._boundHandleInputTrackerStateChanges = this._sendRemoteControlKeyboardSubscriptionNotification.bind(this);

    this._embeddedApp.ports.navigationChangeRequest.subscribe(this._boundNavigationChangeRequest);
    this._embeddedApp.ports.sessionInitiated.subscribe(this._boundHandleSessionInitiated);
    this._embeddedApp.ports.workflowEnded.subscribe(this._boundWorkflowEnded);
    this._embeddedApp.ports.inputTrackerState.subscribe(this._boundHandleInputTrackerStateChanges);
  }

  public unregisterEvents() {
    this._embeddedApp.ports.navigationChangeRequest.unsubscribe(this._boundNavigationChangeRequest);
    this._embeddedApp.ports.sessionInitiated.unsubscribe(this._boundHandleSessionInitiated);
    this._embeddedApp.ports.workflowEnded.unsubscribe(this._boundWorkflowEnded);
    this._embeddedApp.ports.inputTrackerState.unsubscribe(this._boundHandleInputTrackerStateChanges);
  }

  public onDestroyEvents() {
    this._embeddedApp.ports.endSession.send({ forceEndWorkflow: this._workItem.isEnded });
  }

  private _handleNavigationChangeRequest(request: any) {
    this.RxEmitter.send(rcEventRoutes.getRcURL, request);
  }

  private _handleSessionInitiated(sessionData: any) {
    if (this._workItem.remoteControlHistoryId) {
      this._removeExistingRemoteControlSession(this._workItem.id);
      this._clearRemoteItemFromWorkItem(this._workItem);
    } else {
      this._createNewRemoteControlHistoryItem(this._workItem.id, sessionData.sessionId, sessionData.appletUrl);
    }
  }

  private _handleWorkflowEnded(/* reason */) {
    this._removeExistingRemoteControlSession(this._workItem.id);
    this._clearRemoteItemFromWorkItem(this._workItem);
  }

  private _clearRemoteItemFromWorkItem(workItem: ChatItem) {
    workItem.clearHistoryItem();
  }

  private _removeExistingRemoteControlSession(workItemId: string) {
    // TODO: remove if can't be restored
    // Don't trust stored session id, check for existing ones
    return this.bcRestApi.getRemoteHistoryItemsForChat(workItemId)
      .then(data => {
        const activeHistoryItem = this._getActiveRemoteHistoryItem(data);
        if (activeHistoryItem) {
          return this.bcRestApi.closeRemoteControlHistoryItem(activeHistoryItem.chatId, activeHistoryItem.id, RemoteControlCloseTypes.GracefullyClosed);
        }
      });
  }

  private _getActiveRemoteHistoryItem(items) {
    if (!items) {
      return;
    }
    return items.find(item => item.closed === null);
  }

  private _createNewRemoteControlHistoryItem(workItemId: string, remoteSessionId: string, chatRestoreUrl: string) {
    this.bcRestApi.addNewRemoteControlHistoryItem(workItemId, remoteSessionId)
      .then((createHistoryResponse) => {
        this.bcRestApi.startRemoteControlSessionWithAppletUrl(workItemId, createHistoryResponse.id, chatRestoreUrl);
      }, () => {
        // TODO: handle failed case, maybe there's already a session created
        // workItemBold.hasActiveRemoteControl = false;
        throw new Error('Failed to create RC history item');
      });
  }

  private _sendRemoteControlKeyboardSubscriptionNotification(keyboardSubscription: RemoteControlGlobalKeyboardSubscriptionEvent) {
    this.RxEmitter.send(rcEventRoutes.globalKeyboardSubscriptionChange, keyboardSubscription.globalKeyboardSubscription);
  }
}
